import { defineStore } from 'pinia';
import { requests } from '@/common/plugins/axios';
import { CreativeData } from '@/views/creatives/interfaces/creativeData';
import { CanvasPartnerDimensions, LayoutDimension, CanvasVariantsArtBoards, CanvasArtBoards } from '@/common/interfaces/canvasStore';
import { CanvasEditorView, CanvasTypes } from '@/views/creatives/enums';
import { useCompanyStore } from '@/common/store/company';
import { extractDataPath } from '@/views/creatives/utils/canvasPath';
import { getLayerType } from '@/views/creatives/utils/getLayerType';
import { ICanvasSettings, IHighlightedNode, IVariantBoardIds } from '@/views/creatives/interfaces';
import { useEditorStore } from './editor';
import { useCreativeStore } from './creative';
import { ICanvasPartner, ICreativeDimensionBoards } from '../interfaces';

export const useCanvasStore = defineStore('canvas', {
  state: () => ({
    canvasPartnerDimensions: { dimensions: [], partners: [] } as CanvasPartnerDimensions, // Partners that are available to use with canvas
    customDimensions: [] as LayoutDimension[],
    variantsList: {}, // Combination for all instance iterations
    flagged: [], // An array of flagged iterations
    activeLeftDrawerTab: CanvasEditorView.MASTERS as CanvasEditorView,
    editorSelectionType: 'frame',
    masters: [
      {
        name: 'Master portrait',
        width: 400,
        height: 600,
        id: 'M_1',
        isEnabled: true,
      },
      {
        name: 'Master landscape',
        width: 600,
        height: 400,
        id: 'M_2',
        isEnabled: true,
      },
      {
        name: 'Master square',
        width: 400,
        height: 400,
        id: 'M_3',
        isEnabled: true,
      },
    ] as LayoutDimension[], // An array of master objects
    selectedPaths: [] as string[], // The current selected path in the canvas
    oldSortablePath: null, // The previous selected path in the canvas
    currentVariant: null, // if on variantsTab
    sharedSelectedLayer: false, // boolean to check if selection is shared between layers
    masterArtboards: {} as CanvasArtBoards,
    reSizeArtboards: {} as CanvasArtBoards,
    variantArtboards: {} as CanvasVariantsArtBoards,
    debugAverageSavings: { runs: 0, savings: 0 }, // The average savings of the current creative
    showAddCustom: false,
    selectedVariantId: 0 as number | string,
    mouseOverLayerId: '', // This seperates mouse over for expand items Canvas Left Drawer
    selectedPartnerFilter: [],
    highlightedFrameNode: {} as IHighlightedNode, // used when highlighting the frame of an instance
    showEditor: true, // boolean to hide and show editor area
    isImageReset: false,
  }),

  getters: {
    /**
     * get the current creative data to use for frames, instances, iterations, etc
     * @returns {object} - the current creative data property
     */
    canvasData: (): CreativeData => {
      const creativeStore = useCreativeStore();
      return creativeStore.currentCreative.data ?? {} as CreativeData;
    },

    frames: () => {
      const creativeStore = useCreativeStore();
      return creativeStore.currentCreative.data?.frames ?? [];
    },

    boards: () => {
      const creativeStore = useCreativeStore();
      return creativeStore.currentCreative.data?.boards ?? [];
    },

    instances: () => {
      const creativeStore = useCreativeStore();
      return creativeStore.currentCreative.data?.instances ?? {};
    },

    iterations: () => {
      const creativeStore = useCreativeStore();
      return creativeStore.currentCreative.data?.iterations ?? {};
    },

    orders: () => {
      const creativeStore = useCreativeStore();
      return creativeStore.currentCreative.data?.orders ?? {};
    },

    settings: () => {
      const creativeStore = useCreativeStore();
      return creativeStore.currentCreative.settings ?? {} as ICanvasSettings;
    },

    dimensionsToBeExported(): LayoutDimension[] {
      if (!this.settings) return [];
      if (!this.settings.boardsToBeExported) this.settings.boardsToBeExported = [];
      return this.settings.boardsToBeExported?.map((id) => {
        let board = {} as LayoutDimension;
        if (id[0].toLocaleLowerCase() === 'c') {
          board = this.customDimensions.find((o) => o.id === id);
        } else {
          board = this.canvasPartnerDimensions.dimensions.find((o) => o.id === id);
        }
        return board ?? undefined;
      }).filter((o) => o !== undefined);
    },
    variantBoardIdsToExclude: (): IVariantBoardIds => {
      const creativeStore = useCreativeStore();
      if (!creativeStore.currentCreative.settings) creativeStore.currentCreative.settings = { variantBoardIdsToExclude: {} };
      if (!creativeStore.currentCreative.settings?.variantBoardIdsToExclude) creativeStore.currentCreative.settings.variantBoardIdsToExclude = {};
      return creativeStore.currentCreative.settings.variantBoardIdsToExclude;
    },
    getCurrentSelectedPath: (state): string[] => state.selectedPaths,

    getCurrentSelectionIndexes(): string[][] {
      if (!this.getCurrentSelectedPath) return [];
      return this.getCurrentSelectedPath.map((path) => path.match(/([0-9]+)/g));
    },

    getSelectedPathData(): Map<string, string> {
      if (this.getCurrentSelectedPath.length === 0) return new Map();
      return extractDataPath(this.getCurrentSelectedPath[0]);
    },

    getSelectedBoardId(): string {
      const selectedBoard = this.getSelectedPathData.get('masters');
      if (selectedBoard) return selectedBoard;
      if (this.activeLeftDrawerTab === CanvasEditorView.MASTERS) return this.masters[0].id;
      if (this.activeLeftDrawerTab === CanvasEditorView.ARTBOARDS && this.reSizeArtboards.boards) return Object.keys(this.reSizeArtboards.boards)?.[0];
      if (this.activeLeftDrawerTab === CanvasEditorView.VARIANTS && this.variantArtboards.boards) return Object.keys(this.variantArtboards.boards)?.[0];
      return null;
    },

    getSelectedFrameId(): string {
      return this.getSelectedPathData.get('frames') ?? null;
    },

    // TODO: this is unused; remove?
    getSelectedVariantId(): string {
      return this.getSelectedPathData.get('variant') ?? null;
    },

    getSelectedInstanceId(): string {
      return this.getSelectedPathData.get('instances') ?? null;
    },
    getCurrentInstanceType(): CanvasTypes {
      const currentFrameData = this.canvasData.frames?.find((frame) => frame.id === this.getSelectedFrameId);
      return currentFrameData?.type || null;
    },

    getCurrentLayerType(): string {
      if (this.getCurrentSelectedPath.length === 0) return '';
      return getLayerType(this.getCurrentSelectedPath[0]);
    },
    isAllVariantsSelectedForExport() {
      const variantIds = Object.keys(this.variantArtboards);
      if (!variantIds.length) return true;

      return Object.keys(this.variantBoardIdsToExclude).every((boardId) => this.variantBoardIdsToExclude[boardId].length === 0);
    },

  },
  actions: {
    /**
     * Fetch partner templates for canvas
     */
    async fetchCanvasPartners() {
      const result = await requests.get('/types/partners/canvas', {
        id: 'fetch-partners-canvas',
      });

      const canvasPartnerDimensions: CanvasPartnerDimensions = { dimensions: [], partners: [] };
      const enabledDimensions = {};

      result.items.forEach((partner: ICanvasPartner) => {
        if (partner.isEnabled) {
          partner.dimensions.forEach((dimension: ICreativeDimensionBoards) => {
            if (dimension.isEnabled) {
              const dimensionName = dimension.name.split('x').join(' x ');
              if (enabledDimensions[dimensionName]) {
                enabledDimensions[dimensionName].partner.push(partner.name);
              } else {
                enabledDimensions[dimensionName] = {
                  ...dimension,
                  id: `P_${dimension.id}`,
                  masterId: `M_${dimension.masterId}`,
                  name: dimension.name.split('x').join(' x '),
                  partner: [partner.name],
                };
              }
            }
          });
          canvasPartnerDimensions.partners.push({ id: partner.id, name: partner.name });
        }
      });

      // Add all enabled dimensions to dimensions array
      canvasPartnerDimensions.dimensions = Object.values(enabledDimensions);
      canvasPartnerDimensions.dimensions.sort((a, b) => {
        if (a.width === b.width) { return a.height - b.height; } return a.width - b.width;
      });

      // Sort Partners by alphabitical Order for filter display
      canvasPartnerDimensions.partners.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) { return -1; } if (nameA > nameB) { return 1; } return 0;
      });
      this.canvasPartnerDimensions = canvasPartnerDimensions;
    },

    async fetchCustomDimensions() {
      const creativeStore = useCreativeStore();
      const result = await requests.get(`/creatives/dimensions?q=creativeId:eq:${creativeStore.currentCreative.id}`);
      if (result?.items.length === 0) return;
      this.customDimensions = result.items.map((dimension) => ({ ...dimension, id: `C_${dimension.id}`, masterId: `M_${dimension.masterId}` }));
    },

    async addCustomDimensions(width : number, height : number) : Promise<void> {
      const creativeStore = useCreativeStore();
      const companyId = useCompanyStore().currentCompanyId;
      const newCustomDimension = await requests.post('/creatives/dimensions', {
        name: `${width} x ${height}`,
        width: `${width}`,
        height: `${height}`,
        companyId,
        creativeId: creativeStore.currentCreative.id,
      });

      newCustomDimension.id = `C_${newCustomDimension.id}`;
      newCustomDimension.masterId = `M_${newCustomDimension.masterId}`;
      this.customDimensions.push(newCustomDimension);
      creativeStore.currentCreative.settings?.boardsToBeExported.push(newCustomDimension.id);
    },

    setSelectedPath(selectedPath: string | string[]): void {
      const newPaths = (Array.isArray(selectedPath) ? selectedPath : [selectedPath]);
      this.selectedPaths = [...newPaths];
    },

    removeSelectedPath(removedPath: string): void {
      const creativeStore = useCreativeStore();

      const newPaths = this.selectedPaths.filter((path) => path !== removedPath);
      if (newPaths.length === 0) this.setSelectedPath('');
      else this.selectedPaths = [...newPaths];

      creativeStore.showCreativeSettings = false;
    },

    updateActiveLeftDrawerTab(tab: CanvasEditorView) {
      const editorStore = useEditorStore();
      if (tab !== this.activeLeftDrawerTab) {
        editorStore.highlightedNode = {};
        this.highlightedFrameNode = {};
        editorStore.removeAllHighlights();
      }
      this.activeLeftDrawerTab = tab;
    },
    clearVariantsToExclude() {
      const creativeStore = useCreativeStore();
      creativeStore.currentCreative.settings.variantBoardIdsToExclude = {};
    },
    setVariantsToExclude(value) {
      const creativeStore = useCreativeStore();
      creativeStore.currentCreative.settings.variantBoardIdsToExclude = value;
    },
    deselectAllVariantsToExport() {
      const creativeStore = useCreativeStore();
      const variantIds = Object.keys(this.variantArtboards);
      for (let index = 0; index < variantIds.length; index += 1) {
        creativeStore.currentCreative.settings.variantBoardIdsToExclude[variantIds[index]] = [...creativeStore.currentCreative.settings.boardsToBeExported];
      }
    },
  },
});

export default useCanvasStore;
